import { SearchDialog } from '@/components/SearchDialog';
import { Button } from '@/components/ui/button';
import {
DropdownMenu,
DropdownMenuCheckboxItem,
DropdownMenuContent,
DropdownMenuItem,
DropdownMenuLabel,
DropdownMenuSeparator,
DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover,PopoverContent,PopoverTrigger } from '@/components/ui/popover';
import { apiMethods } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { useThemeStore } from '@/store/themeStore';
import { useMutation,useQuery,useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { Bell,Eye,Menu,Monitor,Moon,Search,Sun,User } from 'lucide-react';
import { useEffect,useState } from 'react';
import { useNavigate } from 'react-router';

interface Notification {
  id: string;
  subject: string;
  body: string;
  channel: string;
  priority: string;
  readAt: string | null;
  createdAt: string;
}

export function Header() {
  const navigate = useNavigate();
  const { theme, setTheme, highContrast, toggleHighContrast, toggleMobileNav, mobileNavOpen } = useThemeStore();
  const { user, logout } = useAuthStore();
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const queryClient = useQueryClient();

  // Fetch unread count
  const { data: unreadCountData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: apiMethods.notifications.unreadCount,
    refetchInterval: 60000, // Poll every 60s
  });

  // Fetch notifications list when popover opens
  const { data: notificationsData, isLoading: notificationsLoading } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => apiMethods.notifications.list({ page: 1, limit: 10 }),
    enabled: notificationsOpen,
  });

  // Mark single notification as read
  const markReadMutation = useMutation({
    mutationFn: (id: string) => apiMethods.notifications.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Mark all notifications as read
  const markAllReadMutation = useMutation({
    mutationFn: apiMethods.notifications.markAllRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const unreadCount = (unreadCountData as { data?: { unreadCount?: number } })?.data?.unreadCount ?? 0;
  const notifications: Notification[] = (notificationsData as { data?: Notification[] })?.data ?? [];

  const handleLogout = async () => {
    try {
      await apiMethods.auth.logout();
    } catch {
      // Local sign-out must still finish if the server session is already gone.
    } finally {
      logout();
    }
  };

  // Keyboard shortcut: Ctrl+K or / to open search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      } else if (e.key === '/' && document.activeElement === document.body) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
    <header
      data-testid="app-header"
      className="flex min-h-14 items-center justify-between gap-3 border-b border-border bg-card px-3 py-2 shadow-industrial sm:px-6 print:hidden"
    >
      {/* Mobile nav toggle (below lg; desktop has the persistent sidebar) */}
      <Button
        variant="ghost"
        size="icon"
        className="shrink-0 lg:hidden"
        onClick={toggleMobileNav}
        aria-label={mobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={mobileNavOpen}
        aria-controls="sidebar-nav"
      >
        <Menu className="h-5 w-5" />
      </Button>

      {/* Search */}
      <button
        onClick={() => setSearchOpen(true)}
        className="relative min-w-0 flex-1 text-left sm:max-w-80 sm:flex-none"
      >
        <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <div className="flex min-h-touch items-center overflow-hidden rounded-none border border-border bg-background/50 pl-9 pr-10 text-sm text-muted-foreground shadow-[inset_0_1px_2px_hsl(var(--foreground)/0.1)] transition-colors hover:border-primary/50">
          <span className="truncate font-mono" title="Search items and recipes...">
            Search items and recipes...
          </span>
        </div>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-0.5 text-muted-foreground pointer-events-none">
          <kbd className="text-[10px] font-mono bg-muted px-1.5 py-0.5 border border-border rounded-none">
            /
          </kbd>
        </div>
      </button>

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-1">
        {/* Theme Toggle */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Theme settings">
              {theme === 'dark' || theme === 'oled' ? (
                <Moon className="h-4 w-4" />
              ) : theme === 'system' ? (
                <Monitor className="h-4 w-4" />
              ) : (
                <Sun className="h-4 w-4" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel className="font-mono text-xs uppercase tracking-wide">Theme</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setTheme('light')}>
              <Sun className="mr-2 h-4 w-4" />
              Light
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme('dark')}>
              <Moon className="mr-2 h-4 w-4" />
              Dark
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme('oled')}>
              <Moon className="mr-2 h-4 w-4" />
              OLED Black
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme('system')}>
              <Monitor className="mr-2 h-4 w-4" />
              System
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={highContrast}
              onCheckedChange={toggleHighContrast}
            >
              <Eye className="mr-2 h-4 w-4" />
              High Contrast
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Notifications */}
        <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative" aria-label="Notifications">
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center text-[9px] font-bold text-black bg-primary rounded-none font-mono shadow-[0_0_6px_hsl(var(--primary)/0.4)]">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 max-h-96 overflow-y-auto p-0 rounded-none" align="end">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-4 py-3 bg-muted/30">
              <h3 className="text-sm font-semibold font-mono uppercase tracking-wide">Notifications</h3>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs font-mono"
                  onClick={() => markAllReadMutation.mutate()}
                  disabled={markAllReadMutation.isPending}
                >
                  Mark all read
                </Button>
              )}
            </div>

            {/* Notifications List */}
            <div className="divide-y divide-border">
              {notificationsLoading ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground font-mono">
                  Loading...
                </div>
              ) : notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground font-mono">
                  No notifications
                </div>
              ) : (
                notifications.map((notification) => {
                  const isUnread = !notification.readAt;
                  return (
                    <button
                      key={notification.id}
                      className="w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors relative"
                      onClick={() => {
                        if (isUnread) {
                          markReadMutation.mutate(notification.id);
                        }
                      }}
                    >
                      {isUnread && (
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 bg-primary rounded-none" />
                      )}
                      <div className={isUnread ? 'ml-4' : ''}>
                        <p className="text-sm font-medium line-clamp-1">{notification.subject}</p>
                        {notification.body && (
                          <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                            {notification.body}
                          </p>
                        )}
                        <p className="text-[10px] text-muted-foreground font-mono mt-1">
                          {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                        </p>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </PopoverContent>
        </Popover>

        {/* Separator */}
        <div className="mx-2 h-6 w-px bg-border" />

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="User menu">
              <div
                className="flex h-7 w-7 items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, hsl(var(--primary) / 0.2), hsl(var(--primary) / 0.1))',
                  border: '1px solid hsl(var(--primary) / 0.3)',
                  borderRadius: '2px',
                }}
              >
                {user?.name ? (
                  <span className="text-xs font-bold text-primary" style={{ fontFamily: "'DM Mono', monospace" }}>
                    {user.name
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .toUpperCase()}
                  </span>
                ) : (
                  <User className="h-3.5 w-3.5 text-primary" />
                )}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium">{user?.name || 'Guest User'}</p>
                <p className="text-xs text-muted-foreground font-mono">{user?.email || 'Not signed in'}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')}>Profile Settings</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/settings')}>Preferences</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => void handleLogout()} className="text-destructive">
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>

    {/* Search Dialog */}
    <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
  </>
  );
}
